'use client';
/**
 * Top-right floating toast stack. Pops a card per never-before-seen
 * alert in the current session. Persists until manually closed.
 *
 * Steve directive 2026-05-23: "i would like a popup of the alert on my
 * screen. and left open until i click close." Max 5 visible at once;
 * additional alerts queue silently and surface as earlier ones close.
 *
 * Hidden when alertsEnabled = false (master kill switch).
 */

import { useEffect, useRef, useState } from 'react';
import { useAlerts } from './AlertsContext';
import { useResource } from '@/lib/use-resource';
import type { ActionAlert, ActionAlertsResponse, AlertSeverity } from '@/lib/action-types';

const MAX_VISIBLE = 5;

const SEVERITY_BORDER: Record<AlertSeverity, string> = {
  high:   'border-l-red-500',
  medium: 'border-l-amber-500',
  low:    'border-l-slate-400',
};

const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  high: 'HIGH', medium: 'MED', low: 'LOW',
};

export function AlertsToastStack() {
  const { alertsEnabled, hasSeen, markSeen, resetSeen, openDrawer } = useAlerts();
  const { data } = useResource<ActionAlertsResponse>('/api/action-alerts', 60_000);
  const [visible, setVisible] = useState<ActionAlert[]>([]);
  const initializedRef = useRef(false);

  // On first poll: initialize seen-set to everything that's currently active,
  // so the toast stack doesn't flood with the morning's accumulated alerts on
  // page load. From then on, only NEW alerts pop a toast. setState in effect
  // is the bridge from polled remote data into the rolling visible queue —
  // same exception as useResource.
  useEffect(() => {
    if (!data?.active) return;
    if (!initializedRef.current) {
      const initialIds = data.active.map(a => a.id);
      resetSeen(initialIds);
      initializedRef.current = true;
      return;
    }
    if (!alertsEnabled) return;
    const fresh: ActionAlert[] = [];
    for (const a of data.active) {
      if (!hasSeen(a.id)) {
        markSeen(a.id);
        fresh.push(a);
      }
    }
    if (fresh.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(prev => [...fresh.reverse(), ...prev]);
    }
  }, [data, alertsEnabled, hasSeen, markSeen, resetSeen]);

  if (!alertsEnabled || visible.length === 0) return null;

  const dismissToast = (id: string) => {
    setVisible(prev => prev.filter(a => a.id !== id));
  };
  const dismissAll = () => setVisible([]);

  const shown = visible.slice(0, MAX_VISIBLE);
  const queued = visible.length - shown.length;

  return (
    <div className="fixed top-20 right-4 z-[55] flex flex-col gap-2 max-w-sm pointer-events-none">
      {/* Clear-all bar — always shown when the stack has anything on screen.
          Steve directive: 20+ toasts at once was unworkable when each had to
          be individually dismissed. One click wipes the entire visible queue;
          alerts stay marked seen so they don't re-pop. The AlertsDrawer
          remains the canonical surface for reviewing them. */}
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-md px-3 py-2 flex items-center justify-between gap-2 pointer-events-auto">
        <span className="text-[10px] text-[#94a3b8] font-mono">
          {visible.length} new alert{visible.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={dismissAll}
          className="text-[10px] uppercase tracking-wider font-semibold text-amber-300 hover:text-white px-2 py-0.5 rounded border border-amber-500/40 hover:bg-amber-500/15"
          title="Dismiss every visible toast. Alerts remain reviewable in the drawer."
        >
          × clear all
        </button>
      </div>
      {shown.map(a => (
        <ToastCard key={a.id} alert={a} onClose={() => dismissToast(a.id)} onOpen={() => { dismissToast(a.id); openDrawer(a.symbol); }} />
      ))}
      {queued > 0 && (
        <div className="bg-[#0d1117] border border-[#1e293b] rounded-md px-3 py-2 text-[10px] text-[#94a3b8] text-center font-mono pointer-events-auto">
          + {queued} more queued
        </div>
      )}
    </div>
  );
}

function ToastCard({ alert, onClose, onOpen }: { alert: ActionAlert; onClose: () => void; onOpen: () => void }) {
  const border = SEVERITY_BORDER[alert.severity];
  return (
    <div
      className={`bg-[#0d1117] border ${border} border-l-4 border-[#1e293b] rounded-md p-3 shadow-2xl backdrop-blur-sm pointer-events-auto`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <button
          type="button"
          onClick={onOpen}
          className="flex items-baseline gap-2 text-left flex-1 min-w-0 hover:opacity-80"
          title="Open in drawer"
        >
          <span className="font-mono font-bold text-[13px] text-white">{alert.symbol}</span>
          <span className="text-[9px] uppercase tracking-wider text-[#94a3b8]">{SEVERITY_LABEL[alert.severity]}</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[#64748b] hover:text-white text-lg leading-none px-1 -mt-1"
          aria-label="Close toast"
          title="Close (alert stays in drawer)"
        >
          ×
        </button>
      </div>
      <button type="button" onClick={onOpen} className="block w-full text-left hover:opacity-80">
        <div className="text-[12px] text-[#e2e8f0] leading-snug">{alert.headline}</div>
        {alert.suggestedAction && (
          <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-300 mt-1">▶ {alert.suggestedAction}</div>
        )}
      </button>
    </div>
  );
}
